import React, {useEffect} from 'react';
import {AccessibilityInfo, View} from 'react-native';
import {noop} from 'lodash';

import Icon from '/component/base/Icon';
import Text from '/component/base/Text';
import {SearchAndFilter} from '/constant/TestIDs';
import {useTranslation} from '/provider/TranslationProvider';

import {RecentSearches} from '../RecentSearches';
import {Loading} from '../SearchResultItem/Loading/Loading';
import {SearchResults} from '../SearchResults';

import {useSearchResultsRenderer} from './SearchResultsRenderer.hook';
import {styles} from './SearchResultsRenderer.style';
interface RecordsParams {
  [key: string]: unknown;
}
interface SearchResultsRendererProps {
  renderItem: (args: {isRecentSearch: boolean}) => React.ReactElement;
  searchResults: any[] | null;
  isLoading: boolean;
  hasSearchResults: boolean;
  hasSearchInput: boolean;
  recordsParams: RecordsParams;
}

export const SearchResultsRenderer: React.FC<SearchResultsRendererProps> = ({
  renderItem = noop,
  searchResults = null,
  isLoading = false,
  hasSearchResults = false,
  hasSearchInput = false,
  recordsParams = {},
}) => {
  const {ts} = useTranslation('screen.search');

  const {
    showSearchResults,
    showLoading,
    showNoResultsFound,
    showRecentSearches,
  } = useSearchResultsRenderer({
    isLoading,
    hasSearchResults,
    hasSearchInput,
    searchResults,
  });

  useEffect(() => {
    if (showNoResultsFound) {
      const noResultsMessage = ts('noResults', 'Sorry, no results found');
      const detailMessage = ts(
        'noCoachesOrClients',
        'There are no coaches or clients found for this search.',
      );

      AccessibilityInfo.announceForAccessibility(
        `${noResultsMessage}. ${detailMessage}`,
      );
    }
  }, [showNoResultsFound, ts]);

  return (
    <>
      {showRecentSearches && (
        <RecentSearches
          recordsParams={recordsParams}
          renderItem={renderItem({isRecentSearch: true})}
        />
      )}
      {showLoading && <Loading style={styles.container} />}
      {showSearchResults && (
        <SearchResults
          recordsParams={recordsParams}
          renderItem={renderItem({isRecentSearch: false})}
        />
      )}
      {showNoResultsFound && (
        <View style={styles.noResultsContainer}>
          <Icon name="slim-search" style={styles.icon} accessible={false} />
          <Text
            accessible={true}
            accessibilityLabel={'Sorry, no results found'}
            variant="large"
            style={styles.heading}
            testID={SearchAndFilter.searchResultsLabelNoResults}>
            {ts('noResults', 'Sorry, no results found')}
          </Text>
          <Text
            accessible={true}
            accessibilityLabel={
              'There are no coaches or clients\nfound for this search.'
            }
            style={styles.text}
            testID={SearchAndFilter.searchResultsLabelNoCoachesOrClients}>
            {ts(
              'noCoachesOrClients',
              'There are no coaches or clients\nfound for this search.',
            )}
          </Text>
        </View>
      )}
    </>
  );
};
